import React, { useEffect, useState } from 'react';
import api from '../../api';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import StatusMessage from '../../components/admin/StatusMessage';
import ConfirmationModal from '../../components/admin/ConfirmationModal';
import ImageUploader from '../../components/admin/ImageUploader';

export default function EventsPage() {
  const [eventForm, setEventForm] = useState({
    event_name: '',
    date: '',
    start_time: '',
    end_time: '',
    address: '',
    image_url: '',
    registration_link: '',
    is_paid: false,
    minimum_amount: 25,
    create_registration_form: false,
    include_donation_options: false
  });
  const [eventMsg, setEventMsg] = useState({ type: '', text: '' });
  const [eventsList, setEventsList] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [registrationCounts, setRegistrationCounts] = useState({});
  const [approvalList, setApprovalList] = useState([]);
  const [loadingApproval, setLoadingApproval] = useState(false);
  const [approvalEventId, setApprovalEventId] = useState(null);
  const [registrantList, setRegistrantList] = useState([]);
  const [registrantListsByEvent, setRegistrantListsByEvent] = useState({});
  const [loadingRegistrants, setLoadingRegistrants] = useState(false);
  const [registrantEventId, setRegistrantEventId] = useState(null);
  const [registrantSort, setRegistrantSort] = useState({ field: 'name', direction: 'asc' });
  const [expandedEvents, setExpandedEvents] = useState({});
  const [cancelEventTarget, setCancelEventTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingEvent, setCancellingEvent] = useState(false);
  const [reportLoadingId, setReportLoadingId] = useState(null);

  useEffect(() => {
    loadEvents();
    loadRegistrationCounts();
  }, []);

  // Filter events into upcoming and past based on date_time
  useEffect(() => {
    const now = new Date();
    const upcoming = eventsList.filter((ev) => new Date(ev.date_time) > now);
    const past = eventsList.filter((ev) => new Date(ev.date_time) <= now);
    setUpcomingEvents(upcoming);
    setPastEvents(past);
  }, [eventsList]);

  const loadEvents = () => {
    api.get('/api/admin/events?include_past=true')
      .then((r) => setEventsList(r.data || []))
      .catch(() => setEventMsg({ type: 'error', text: 'Failed to load events' }));
  };

  const loadRegistrationCounts = () => {
    api.get('/api/admin/events?include_past=true')
      .then((r) => {
        const counts = {};
        (r.data || []).forEach(event => {
          counts[event.id] = {
            adults: event.adults_count || 0,
            kids: event.kids_count || 0
          };
        });
        setRegistrationCounts(counts);
      })
      .catch(() => {});
  };

  const loadApprovalList = async (eventId) => {
    setApprovalEventId(eventId);
    setLoadingApproval(true);
    try {
      const response = await api.get('/api/admin/events/' + eventId + '/registrations');
      setApprovalList(response.data || []);
    } catch (err) {
      setEventMsg({ type: 'error', text: 'Failed to load approval list' });
    } finally {
      setLoadingApproval(false);
    }
  };

  const loadRegistrants = async (eventId) => {
    const isAlreadyOpen = registrantEventId === eventId;
    if (isAlreadyOpen) {
      setRegistrantEventId(null);
      setRegistrantList([]);
      return;
    }

    setRegistrantEventId(eventId);
    setLoadingRegistrants(true);
    try {
      const response = await api.get('/api/admin/events/' + eventId + '/registrations');
      const eventRegistrants = response.data || [];
      setRegistrantList(eventRegistrants);
      setRegistrantListsByEvent((prev) => ({ ...prev, [eventId]: eventRegistrants }));
    } catch (err) {
      setEventMsg({ type: 'error', text: 'Failed to load registrants' });
    } finally {
      setLoadingRegistrants(false);
    }
  };

  const getRegistrantSortValue = (registrant, field) => {
    switch (field) {
      case 'adults':
      case 'kids':
        return Number(registrant[field] ?? 0);
      case 'volunteer':
        return registrant.volunteering || registrant.volunteer ? 'Yes' : 'No';
      case 'status':
        return registrant.is_approved ? 'Approved' : (registrant.is_paid === false ? 'Auto-approved' : 'Pending');
      case 'phone':
        return registrant.mobile || registrant.phone || '';
      default:
        return registrant[field] || '';
    }
  };

  const sortRegistrants = (field) => {
    setRegistrantSort((currentSort) => ({
      field,
      direction: currentSort.field === field && currentSort.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const activeRegistrantList = registrantEventId ? (registrantListsByEvent[registrantEventId] || registrantList) : [];
  const sortedRegistrantList = [...activeRegistrantList].sort((first, second) => {
    const firstValue = getRegistrantSortValue(first, registrantSort.field);
    const secondValue = getRegistrantSortValue(second, registrantSort.field);
    const comparison = typeof firstValue === 'number' && typeof secondValue === 'number'
      ? firstValue - secondValue
      : String(firstValue).localeCompare(String(secondValue), undefined, { sensitivity: 'base' });
    return registrantSort.direction === 'asc' ? comparison : -comparison;
  });

  const renderRegistrantSortButton = (field, label) => (
    <button
      type="button"
      onClick={() => sortRegistrants(field)}
      aria-label={`Sort registrants by ${label}`}
      style={{ border: 0, background: 'transparent', padding: 0, font: 'inherit', cursor: 'pointer' }}
    >
      {label} ({registrantSort.field === field ? registrantSort.direction : 'sort'})
    </button>
  );

  const showMsg = (type, text, duration = 3000) => {
    setEventMsg({ type, text });
    setTimeout(() => setEventMsg({ type: '', text: '' }), duration);
  };

  const handleImageUploadSuccess = (data) => {
    const url = data.media_url || data;
    setEventForm((f) => ({ ...f, image_url: url }));
    setImagePreview(url);
    showMsg('success', 'Image uploaded successfully');
  };

  const handleImageUploadError = (error) => {
    showMsg('error', 'Upload failed: ' + (error.response?.data?.detail || error.message));
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setEventMsg({ type: '', text: '' });
    try {
      const form = new FormData();
      form.append('event_name', eventForm.event_name);
      // Build datetime from date and start_time
      const dateTimeStr = eventForm.date + 'T' + eventForm.start_time + ':00';
      form.append('date_time', dateTimeStr);
      
      // Build end_time from date and end_time if provided
      if (eventForm.end_time) {
        const endTimeStr = eventForm.date + 'T' + eventForm.end_time + ':00';
        form.append('end_time', endTimeStr);
      }
      
      form.append('address', eventForm.address);
      if (eventForm.image_url) form.append('image_url', eventForm.image_url);
      
      // Add paid event fields
      form.append('is_paid', eventForm.is_paid);
      if (eventForm.is_paid) {
        form.append('minimum_amount', eventForm.minimum_amount);
      }
      form.append('include_donation_options', eventForm.include_donation_options);

      if (editingEvent) {
        // When editing with registration form checked, auto-generate link
        const regLink = eventForm.create_registration_form
          ? window.location.origin + '/register/' + editingEvent.id
          : eventForm.registration_link;
        if (regLink) form.append('registration_link', regLink);
        
        await api.put('/api/admin/events/' + editingEvent.id, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        showMsg('success', 'Event updated successfully');
      } else {
        // Create new event first to get the ID
        const response = await api.post('/api/admin/events', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        // If registration form was requested, update the event with the registration link
        if (eventForm.create_registration_form && response.data) {
          const eventId = response.data.id;
          const updateForm = new FormData();
          updateForm.append('registration_link', window.location.origin + '/register/' + eventId);
          updateForm.append('event_name', eventForm.event_name);
                    const updateDateTimeStr = eventForm.date + 'T' + eventForm.start_time + ':00';
          updateForm.append('date_time', updateDateTimeStr);
          if (eventForm.end_time) {
            const updateEndTimeStr = eventForm.date + 'T' + eventForm.end_time + ':00';
            updateForm.append('end_time', updateEndTimeStr);
          }
          updateForm.append('address', eventForm.address);
          updateForm.append('is_paid', eventForm.is_paid);
          if (eventForm.is_paid) {
            updateForm.append('minimum_amount', eventForm.minimum_amount);
            updateForm.append('include_donation_options', eventForm.include_donation_options);
          }
          await api.put('/api/admin/events/' + eventId, updateForm, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
        showMsg('success', 'Event created successfully');
      }
      
      loadEvents();
      loadRegistrationCounts();
      resetForm();
    } catch (err) {
      showMsg('error', 'Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const resetForm = () => {
    setEventForm({
      event_name: '',
      date: '',
      start_time: '',
      end_time: '',
      address: '',
      image_url: '',
      registration_link: '',
      is_paid: false,
      minimum_amount: 25,
      create_registration_form: false,
      include_donation_options: false
    });
    setEditingEvent(null);
    setImagePreview('');
  };

  const startEditEvent = (ev) => {
    setEditingEvent(ev);
    setEventForm({
      event_name: ev.event_name,
      date_time: ev.date_time ? ev.date_time.slice(0, 16) : '',
      address: ev.address,
      image_url: ev.image_url || '',
      registration_link: ev.registration_link || '',
      is_paid: ev.is_paid || false,
      minimum_amount: ev.minimum_amount || 25,
            create_registration_form: !!(ev.registration_link && ev.registration_link.includes('/register/')),
      include_donation_options: ev.include_donation_options || false
    });
    // Parse date_time to populate new fields
    if (ev.date_time) {
      const dt = new Date(ev.date_time);
      setEventForm(prev => ({
        ...prev,
        date: dt.toISOString().slice(0, 10),
        start_time: dt.toISOString().slice(11, 16),
        end_time: ev.end_time ? new Date(ev.end_time).toISOString().slice(11, 16) : ''
      }));
    }
    setImagePreview(ev.image_url || '');
    
    // Load approval list if it is a paid event
    if (ev.is_paid) {
      loadApprovalList(ev.id);
    }
  };

    const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete('/api/admin/events/' + deleteTarget.id);
      showMsg('success', 'Event deleted successfully');
      loadEvents();
      loadRegistrationCounts();
    } catch (err) {
      showMsg('error', 'Failed to delete: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeleteTarget(null);
    }
  };

  const startCancelEvent = (ev) => {
    setCancelEventTarget(ev);
    setCancelReason('');
  };

  const confirmCancelEvent = async () => {
    if (!cancelEventTarget || !cancelReason.trim()) {
      showMsg('error', 'Please provide a cancellation reason');
      return;
    }

    setCancellingEvent(true);
    try {
      const form = new URLSearchParams();
      form.append('reason', cancelReason);

      const response = await api.post('/api/admin/events/' + cancelEventTarget.id + '/cancel-event', form, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      showMsg('success', 'Event cancelled and notifications sent to ' + response.data.registrations_notified + ' registrants');
      loadEvents();
      loadRegistrationCounts();
      setCancelEventTarget(null);
      setCancelReason('');
    } catch (err) {
      showMsg('error', 'Failed to cancel event: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCancellingEvent(false);
    }
  };

    const handleEventReport = async (eventId) => {
    setReportLoadingId(eventId);
    try {
      // Load registrations data
      const regResponse = await api.get('/api/admin/events/' + eventId + '/registrations');
      const event = eventsList.find((item) => item.id === eventId);
      const includeStatus = event?.is_paid === true;
      const registrations = [...(regResponse.data || [])].sort((first, second) => {
        const firstName = String(first.name || '').trim();
        const secondName = String(second.name || '').trim();
        if (!firstName && secondName) return 1;
        if (firstName && !secondName) return -1;
        return firstName.localeCompare(secondName, undefined, { sensitivity: 'base' });
      });
      
      if (registrations.length === 0) {
        showMsg('error', 'No registrations found for this event');
        return;
      }
      
      // Generate CSV content
      const csvRows = [includeStatus
        ? 'Name,Email,Phone,Adults,Kids,Volunteer,Status'
        : 'Name,Email,Phone,Adults,Kids,Volunteer'];
      registrations.forEach(reg => {
        const name = reg.name || '';
        const email = reg.email || '';
        const phone = reg.mobile || reg.phone || '';
        const adults = reg.adults ?? 0;
        const kids = reg.kids ?? 0;
        const volunteer = reg.volunteering || reg.volunteer ? 'Yes' : 'No';
        const status = reg.is_approved ? 'Approved' : (reg.is_paid === false ? 'Auto-approved' : 'Pending');
        // Escape commas and quotes in CSV fields
        const escapeCsv = (str) => `"${String(str).replace(/"/g, '""')}"`;
        const row = `${escapeCsv(name)},${escapeCsv(email)},${escapeCsv(phone)},${adults},${kids},${volunteer}`;
        csvRows.push(includeStatus ? `${row},${escapeCsv(status)}` : row);
      });
      const csvContent = csvRows.join('\n');
      
      // Trigger CSV download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registrations_event_${eventId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Also email the report to the current user
      const emailResponse = await api.post('/api/admin/events/' + eventId + '/report-email');
      
      showMsg('success', 'CSV downloaded and report emailed to you!');
    } catch (err) {
      showMsg('error', 'Failed to generate report: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReportLoadingId(null);
    }
  };

  const approveRegistration = async (regId, eventId) => {
    try {
      await api.patch('/api/admin/registrations/' + regId + '/approve');
      showMsg('success', 'Registration approved');
      loadApprovalList(eventId);
      loadRegistrationCounts();
    } catch (err) {
      showMsg('error', 'Failed to approve: ' + (err.response?.data?.detail || err.message));
    }
  };

  const rejectRegistration = async (regId, eventId) => {
    try {
      await api.patch('/api/admin/registrations/' + regId + '/reject');
      showMsg('success', 'Registration rejected');
      loadApprovalList(eventId);
      loadRegistrationCounts();
    } catch (err) {
      showMsg('error', 'Failed to reject: ' + (err.response?.data?.detail || err.message));
    }
  };

  const toggleExpand = (eventId) => {
    const willExpand = !expandedEvents[eventId];
    setExpandedEvents((prev) => ({
      ...prev,
      [eventId]: willExpand
    }));

    // Load approval list when expanding a paid event
    if (willExpand) {
      const ev = eventsList.find((e) => e.id === eventId);
      if (ev && ev.is_paid) {
        loadApprovalList(eventId);
      }
    }
  };

  return (
    <div className="admin-events-page">
      <AdminSectionHeader title="Event Management" />
      <StatusMessage msg={eventMsg} />

      {/* Event Form */}
      <section className="admin-section">
        <h3>Create/Edit Event</h3>
        <form onSubmit={handleEventSubmit} className="event-form">
          <div className="form-group">
            <label>Event Name:*</label>
            <input
              type="text"
              required
              value={eventForm.event_name}
              onChange={(e) => setEventForm((f) => ({ ...f, event_name: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Date:*</label>
            <input
              type="date"
              required
              value={eventForm.date}
              onChange={(e) => setEventForm((f) => ({ ...f, date: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Start Time:*</label>
            <input
              type="time"
              required
              value={eventForm.start_time}
              onChange={(e) => setEventForm((f) => ({ ...f, start_time: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>End Time:*</label>
            <input
              type="time"
              required
              value={eventForm.end_time}
              onChange={(e) => setEventForm((f) => ({ ...f, end_time: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Address:*</label>
            <input
              type="text"
              required
              value={eventForm.address}
              onChange={(e) => setEventForm((f) => ({ ...f, address: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <div className="event-checkbox-control">
              <label className="checkbox-label" htmlFor="create-registration-form">
              <input
                id="create-registration-form"
                type="checkbox"
                checked={eventForm.create_registration_form}
                onChange={(e) => setEventForm((f) => ({ ...f, create_registration_form: e.target.checked }))}
              />
              Create Registration Form
              </label>
              <span className="form-hint event-checkbox-hint">
                When checked, a registration form will be created at /register/{'{event_id}'}
              </span>
            </div>
          </div>

          <div className="form-group">
            <div className="event-checkbox-control">
             <label className="checkbox-label" htmlFor="paid-event">
              <input
                id="paid-event"
                type="checkbox"
                checked={eventForm.is_paid}
                onChange={(e) => setEventForm((f) => ({ ...f, is_paid: e.target.checked, include_donation_options: false }))}
              />
              Paid Event
            </label>
            </div>
          </div>

          {eventForm.is_paid && (
            <div className="form-group">
              <label>Minimum Amount ($)</label>
              <select
                className="paid-event-amount-select"
                value={eventForm.minimum_amount}
                onChange={(e) => setEventForm((f) => ({ ...f, minimum_amount: parseInt(e.target.value) }))}
              >
                <option value={25}>$25</option>
                <option value={50}>$50</option>
                <option value={100}>$100</option>
                <option value={200}>$200</option>
                <option value={500}>$500</option>
                <option value={1000}>$1000</option>
              </select>
              <span className="form-hint" style={{ display: 'block', marginTop: '0.5rem' }}>
                Payment details will be shown on the registration page
              </span>
            </div>
          )}

          <div className="form-group">
            <div className="event-checkbox-control">
              <label className="checkbox-label" htmlFor="include-donation-options">
              <input
                id="include-donation-options"
                type="checkbox"
                checked={eventForm.include_donation_options}
                onChange={(e) => setEventForm((f) => ({ ...f, include_donation_options: e.target.checked }))}
                disabled={eventForm.is_paid}
              />
              Include Donation Options
              </label>
              <span className={`form-hint event-checkbox-hint ${eventForm.is_paid ? 'is-disabled' : ''}`}>
                When checked, donation links and QR codes will be shown on the registration page
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>Registration Link (optional)</label>
            <input
              type="url"
              value={eventForm.registration_link}
              onChange={(e) => setEventForm((f) => ({ ...f, registration_link: e.target.value }))}
              placeholder="https://example.com/register"
            />
          </div>

          <div className="form-group">
            <label>Event Image</label>
            <ImageUploader
              onUploadSuccess={handleImageUploadSuccess}
              onUploadError={handleImageUploadError}
              label="Upload Event Image"
            />
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Event preview"
                className="event-image-preview"
              />
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <button type="submit">{editingEvent ? 'Update Event' : 'Create Event'}</button>
            {editingEvent && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </section>

      {/* Events List with Accordion and Registration Counts */}
      <section className="admin-section">
        <h3>Upcoming Events</h3>
        {upcomingEvents.length === 0 ? (
          <p className="empty-state">No upcoming events.</p>
        ) : (
          <div className="events-list-accordion">
            {upcomingEvents.map((ev) => {
              const counts = registrationCounts[ev.id] || { adults: 0, kids: 0 };
              const isExpanded = expandedEvents[ev.id];
              return (
                <div key={ev.id} className="accordion">
                  <div
                    className={`accordion-header ${isExpanded ? 'active' : ''}`}
                    onClick={() => toggleExpand(ev.id)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '1rem' }}>{ev.event_name}</strong>
                                            <small style={{ color: 'var(--muted)', wordBreak: 'break-all' }}>
                        {new Date(ev.date_time).toLocaleString()}{ev.end_time && ` - ${new Date(ev.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                      </small>
                      {ev.is_paid && (
                        <span className="event-paid-badge" style={{ margin: 0 }}>
                          Paid - Min: ${ev.minimum_amount || 0}
                        </span>
                      )}
                      {ev.is_cancelled && (
                        <span className="event-cancelled-badge" style={{ 
                          background: '#f8d7da', 
                          color: '#721c24', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '9999px', 
                          fontSize: '0.75rem', 
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          marginLeft: '0.5rem'
                        }}>
                          Cancelled
                        </span>
                      )}
                      <span style={{
                        display: 'inline-flex',
                        gap: '0.25rem',
                        marginLeft: 'auto',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{
                          background: '#dbeafe',
                          color: '#1e40af',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}>
                          👨 {counts.adults}
                        </span>
                        <span style={{
                          background: '#fce7f3',
                          color: '#9d174d',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}>
                          👶 {counts.kids}
                        </span>
                      </span>
                    </div>
                    <i className="fa-solid fa-chevron-down"></i>
                  </div>
                  <div className={`accordion-content ${isExpanded ? 'open' : ''}`}>
                    <div className="event-details" style={{ marginBottom: '1rem' }}>
                      <small>{ev.address}</small>
                      {ev.date_time && (
                        <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--muted)' }}>
                          Start Time: {new Date(ev.date_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </small>
                      )}
                      {ev.end_time && (
                        <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--muted)' }}>
                          End Time: {new Date(ev.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </small>
                      )}
                      {ev.registration_link && (
                        <small style={{ display: 'block', marginTop: '0.25rem', wordBreak: 'break-all' }}>
                          Registration:{' '}
                          <a href={ev.registration_link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                            {ev.registration_link}
                          </a>
                          {' '}
                          <button
                            type="button"
                            className="btn-copy-link"
                            onClick={() => {
                              try {
                                const linkToCopy = new URL(ev.registration_link, window.location.origin).href;
                                navigator.clipboard.writeText(linkToCopy);
                                showMsg('success', 'Link copied to clipboard!');
                              } catch (err) {
                                showMsg('error', 'Invalid link');
                              }
                            }}
                            title="Copy registration link"
                          >
                            <i className="fa-solid fa-copy"></i>
                          </button>
                        </small>
                      )}
                      {ev.image_url && (
                        <img src={ev.image_url} alt={ev.event_name} className="event-thumb" style={{ maxWidth: '120px', maxHeight: '90px', display: 'block', marginTop: '0.5rem' }} />
                      )}
                    </div>

                    {/* Registration Approvals - shown for all paid events, not just when editing */}
                    {ev.is_paid && (
                      <div style={{ overflowX: 'auto' }}>
                        <h4 style={{ margin: '0.5rem 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600 }}>
                          Registration Approvals
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ marginLeft: '1rem' }}
                            onClick={() => loadApprovalList(ev.id)}
                          >
                            {loadingApproval && approvalEventId === ev.id ? 'Refreshing...' : 'Refresh'}
                          </button>
                        </h4>
                        {loadingApproval && approvalEventId === ev.id ? (
                          <p>Loading...</p>
                        ) : approvalList.length === 0 ? (
                          <p>No registrations yet.</p>
                        ) : (
                          <table className="approval-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Adults</th>
                                <th>Kids</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {approvalList.map((reg) => (
                                <tr key={reg.id}>
                                  <td>{reg.name}</td>
                                  <td>{reg.email}</td>
                                  <td>{reg.adults}</td>
                                  <td>{reg.kids}</td>
                                  <td>
                                    {reg.is_approved ? (
                                      <span className="status approved">Approved</span>
                                    ) : (
                                      <span className="status pending">Pending</span>
                                    )}
                                  </td>
                                  <td>
                                    {reg.is_paid === false ? (
                                      <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Auto-approved</span>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          disabled={reg.is_approved}
                                          onClick={() => !reg.is_approved && approveRegistration(reg.id, ev.id)}
                                        >
                                          Approve
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          style={{ marginLeft: '0.25rem' }}
                                          disabled={reg.is_approved}
                                          onClick={() => !reg.is_approved && rejectRegistration(reg.id, ev.id)}
                                        >
                                          Reject
                                        </button>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={ev.is_cancelled}
                        onClick={() => startEditEvent(ev)}
                        title="Edit event"
                        style={ev.is_cancelled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      >
                        <i className="fa-solid fa-pen"></i> Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-info"
                        onClick={() => loadRegistrants(ev.id)}
                        title="View registrants"
                      >
                        <i className="fa-solid fa-users"></i> Registrants
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-info"
                        onClick={() => handleEventReport(ev.id)}
                        disabled={reportLoadingId === ev.id}
                        title="Download Registration Report"
                      >
                        {reportLoadingId === ev.id ? (
                          <><i className="fa-solid fa-spinner fa-spin"></i> Generating...</>
                        ) : (
                          <><i className="fa-solid fa-file-excel"></i> Report</>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-warning"
                        disabled={ev.is_cancelled}
                        onClick={() => startCancelEvent(ev)}
                        title="Cancel Event"
                        style={ev.is_cancelled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      >
                        <i className="fa-solid fa-ban"></i> Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => setDeleteTarget(ev)}
                        title="Delete event"
                      >
                        <i className="fa-solid fa-trash"></i> Delete
                      </button>
                    </div>

                    {registrantEventId === ev.id && (
                      <div className="registrants-panel">
                        <h4 style={{ margin: '0.5rem 0 0.75rem 0', fontSize: '0.9rem', fontWeight: 600 }}>
                          Registrants
                        </h4>
                        {loadingRegistrants ? (
                          <p>Loading registrants...</p>
                        ) : (registrantListsByEvent[ev.id] || []).length === 0 ? (
                          <p>No registrants yet.</p>
                        ) : (
                          <div className="registrants-table-scroll">
                            <table className="approval-table">
                              <thead>
                                <tr>
                                  <th>{renderRegistrantSortButton('name', 'Name')}</th>
                                  <th>{renderRegistrantSortButton('email', 'Email')}</th>
                                  <th>{renderRegistrantSortButton('phone', 'Phone')}</th>
                                  <th>{renderRegistrantSortButton('adults', 'Adults')}</th>
                                  <th>{renderRegistrantSortButton('kids', 'Kids')}</th>
                                  <th>{renderRegistrantSortButton('volunteer', 'Volunteer')}</th>
                                  {ev.is_paid && <th>{renderRegistrantSortButton('status', 'Status')}</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {sortedRegistrantList.map((reg) => (
                                  <tr key={reg.id}>
                                    <td>{reg.name || '—'}</td>
                                    <td>{reg.email || '—'}</td>
                                    <td>{reg.mobile || reg.phone || '—'}</td>
                                    <td>{reg.adults ?? 0}</td>
                                    <td>{reg.kids ?? 0}</td>
                                    <td>{reg.volunteering || reg.volunteer ? 'Yes' : 'No'}</td>
                                    {ev.is_paid && (
                                      <td>
                                        {reg.is_approved ? 'Approved' : (reg.is_paid === false ? 'Auto-approved' : 'Pending')}
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>


      {/* Past Events List */}
      <section className="admin-section">
        <h3>Past Events</h3>
        {pastEvents.length === 0 ? (
          <p className="empty-state">No past events found.</p>
        ) : (
          <div className="events-list-accordion">
            {pastEvents.map((ev) => {
              const counts = registrationCounts[ev.id] || { adults: 0, kids: 0 };
              const isExpanded = expandedEvents[ev.id];
              return (
                <div key={ev.id} className="accordion">
                  <div
                    className={`accordion-header ${isExpanded ? 'active' : ''}`}
                    onClick={() => toggleExpand(ev.id)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '1rem' }}>{ev.event_name}</strong>
                      <small style={{ color: 'var(--muted)', wordBreak: 'break-all' }}>
                        {new Date(ev.date_time).toLocaleString()}{ev.end_time && ` - ${new Date(ev.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                      </small>
                      {ev.is_paid && (
                        <span className="event-paid-badge" style={{ margin: 0 }}>
                          Paid - Min: ${ev.minimum_amount || 0}
                        </span>
                      )}
                      {ev.is_cancelled && (
                        <span className="event-cancelled-badge" style={{ 
                          background: '#f8d7da', 
                          color: '#721c24', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '9999px', 
                          fontSize: '0.75rem', 
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          marginLeft: '0.5rem'
                        }}>
                          Cancelled
                        </span>
                      )}
                      <span style={{
                        display: 'inline-flex',
                        gap: '0.25rem',
                        marginLeft: 'auto',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{
                          background: '#dbeafe',
                          color: '#1e40af',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}>
                          👨 {counts.adults}
                        </span>
                        <span style={{
                          background: '#fce7f3',
                          color: '#9d174d',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}>
                          👶 {counts.kids}
                        </span>
                      </span>
                    </div>
                    <i className="fa-solid fa-chevron-down"></i>
                  </div>
                  <div className={`accordion-content ${isExpanded ? 'open' : ''}`}>
                    <div className="event-details" style={{ marginBottom: '1rem' }}>
                      <small>{ev.address}</small>
                      {ev.date_time && (
                        <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--muted)' }}>
                          Start Time: {new Date(ev.date_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </small>
                      )}
                      {ev.end_time && (
                        <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--muted)' }}>
                          End Time: {new Date(ev.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </small>
                      )}
                      {ev.registration_link && (
                        <small style={{ display: 'block', marginTop: '0.25rem', wordBreak: 'break-all' }}>
                          Registration:{' '}
                          <a href={ev.registration_link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                            {ev.registration_link}
                          </a>
                          {' '}
                          <button
                            type="button"
                            className="btn-copy-link"
                            onClick={() => {
                              try {
                                const linkToCopy = new URL(ev.registration_link, window.location.origin).href;
                                navigator.clipboard.writeText(linkToCopy);
                                showMsg('success', 'Link copied to clipboard!');
                              } catch (err) {
                                showMsg('error', 'Invalid link');
                              }
                            }}
                            title="Copy registration link"
                          >
                            <i className="fa-solid fa-copy"></i>
                          </button>
                        </small>
                      )}
                      {ev.image_url && (
                        <img src={ev.image_url} alt={ev.event_name} className="event-thumb" style={{ maxWidth: '120px', maxHeight: '90px', display: 'block', marginTop: '0.5rem' }} />
                      )}
                    </div>

                    {ev.is_paid && (
                      <div style={{ overflowX: 'auto' }}>
                        <h4 style={{ margin: '0.5rem 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600 }}>
                          Registration Approvals
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ marginLeft: '1rem' }}
                            onClick={() => loadApprovalList(ev.id)}
                          >
                            {loadingApproval && approvalEventId === ev.id ? 'Refreshing...' : 'Refresh'}
                          </button>
                        </h4>
                        {loadingApproval && approvalEventId === ev.id ? (
                          <p>Loading...</p>
                        ) : approvalList.length === 0 ? (
                          <p>No registrations yet.</p>
                        ) : (
                          <table className="approval-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Adults</th>
                                <th>Kids</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {approvalList.map((reg) => (
                                <tr key={reg.id}>
                                  <td>{reg.name}</td>
                                  <td>{reg.email}</td>
                                  <td>{reg.adults}</td>
                                  <td>{reg.kids}</td>
                                  <td>
                                    {reg.is_approved ? (
                                      <span className="status approved">Approved</span>
                                    ) : (
                                      <span className="status pending">Pending</span>
                                    )}
                                  </td>
                                  <td>
                                    {reg.is_paid === false ? (
                                      <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Auto-approved</span>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          disabled={reg.is_approved}
                                          onClick={() => !reg.is_approved && approveRegistration(reg.id, ev.id)}
                                        >
                                          Approve
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          style={{ marginLeft: '0.25rem' }}
                                          disabled={reg.is_approved}
                                          onClick={() => !reg.is_approved && rejectRegistration(reg.id, ev.id)}
                                        >
                                          Reject
                                        </button>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-info"
                        onClick={() => loadRegistrants(ev.id)}
                        title="View registrants"
                      >
                        <i className="fa-solid fa-users"></i> Registrants
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-info"
                        onClick={() => handleEventReport(ev.id)}
                        disabled={reportLoadingId === ev.id}
                        title="Download Registration Report"
                      >
                        {reportLoadingId === ev.id ? (
                          <><i className="fa-solid fa-spinner fa-spin"></i> Generating...</>
                        ) : (
                          <><i className="fa-solid fa-file-excel"></i> Report</>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => setDeleteTarget(ev)}
                        title="Delete event"
                      >
                        <i className="fa-solid fa-trash"></i> Delete
                      </button>
                    </div>

                    {registrantEventId === ev.id && (
                      <div className="registrants-panel">
                        <h4 style={{ margin: '0.5rem 0 0.75rem 0', fontSize: '0.9rem', fontWeight: 600 }}>
                          Registrants
                        </h4>
                        {loadingRegistrants ? (
                          <p>Loading registrants...</p>
                        ) : (registrantListsByEvent[ev.id] || []).length === 0 ? (
                          <p>No registrants yet.</p>
                        ) : (
                          <div className="registrants-table-scroll">
                            <table className="approval-table">
                              <thead>
                                <tr>
                                  <th>{renderRegistrantSortButton('name', 'Name')}</th>
                                  <th>{renderRegistrantSortButton('email', 'Email')}</th>
                                  <th>{renderRegistrantSortButton('phone', 'Phone')}</th>
                                  <th>{renderRegistrantSortButton('adults', 'Adults')}</th>
                                  <th>{renderRegistrantSortButton('kids', 'Kids')}</th>
                                  <th>{renderRegistrantSortButton('volunteer', 'Volunteer')}</th>
                                  {ev.is_paid && <th>{renderRegistrantSortButton('status', 'Status')}</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {sortedRegistrantList.map((reg) => (
                                  <tr key={reg.id}>
                                    <td>{reg.name || '—'}</td>
                                    <td>{reg.email || '—'}</td>
                                    <td>{reg.mobile || reg.phone || '—'}</td>
                                    <td>{reg.adults ?? 0}</td>
                                    <td>{reg.kids ?? 0}</td>
                                    <td>{reg.volunteering || reg.volunteer ? 'Yes' : 'No'}</td>
                                    {ev.is_paid && (
                                      <td>
                                        {reg.is_approved ? 'Approved' : (reg.is_paid === false ? 'Auto-approved' : 'Pending')}
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Delete Event"
        message={`Are you sure you want to delete ${deleteTarget?.event_name || 'Unknown'}?`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />

      {/* Event Cancellation Modal */}
      {cancelEventTarget && (
        <div className="modal-overlay" onClick={() => setCancelEventTarget(null)} role="dialog" aria-modal="true">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Cancel Event</h3>
              <button type="button" className="modal-close" onClick={() => setCancelEventTarget(null)} aria-label="Close">
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem' }}>
                <strong>{cancelEventTarget.event_name}</strong> will be cancelled and all registered participants will receive an email notification.
              </p>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Cancellation Reason *
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Please provide a reason for the cancellation (this will be sent to all registrants)..."
                rows={4}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical', fontSize: '0.9rem' }}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setCancelEventTarget(null)}>
                Back
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmCancelEvent}
                disabled={cancellingEvent || !cancelReason.trim()}
                style={{ background: '#dc3545', borderColor: '#dc3545' }}
              >
                {cancellingEvent ? 'Cancelling...' : 'Cancel Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
